  /**
   * Supervisor Injury Report Component
   * ===============================
   * 
   * Individual form component 
   * 
   * Base Components:
   * - EXTENDS BaseFormComponent<SupervisorInjuryReport> for form logic inheritance
   * - USES FormLayoutComponent for step navigation UI (in template)
   * 
   * Form Configuration:
   * - Form structure, steps, sections, and field organization are defined in the form model
   * - Signature and upload requirements are configured via form model metadata
   * - Component implements abstract methods: initializeForm() and getFormData()
   * - Component overrides setupDefaultStepValidation() for custom step validation rules
   * 
   * Key Functionality:
   * - Creates nested form groups for logical organization (employeeDetails, jobDetails, incidentDetails, incidentAnalysis, injuryAssessment, signOff)
   * - Implements checkbox group validation requiring at least 1 incident cause selection
   * - Provides comprehensive incident reporting with employee and job details
   * - Implements conditional validation for multiple fields based on user selections
   * - Implements form validation for safety compliance and incident documentation
   * - Implements step validation with attachment and signature requirements
   * - Flattens nested form structure for backend submission
   * 
   * Unique Complex Logic:
   * - Multiple conditional validation setups for fields that become required based on other field values
   * - Checkbox group validation using custom validator requiring minimum selection
   * - Real-time validation updates for conditional fields with subscription management
   * - Medical treatment validation requiring multiple fields when treatment is sought
   * 
   * Form Model vs Component Responsibilities:
   * - FORM MODEL: Defines step/section structure, field mappings, attachment requirements, and form metadata
   * - COMPONENT: Handles Angular form validation, form group creation, conditional field logic, and overrides base form methods
   * 
   * @author MacSafety Development Team
   * @version 1.0
   */

import { Component, OnInit } from '@angular/core';
import { FormBuilder, Validators, FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { BaseFormComponent } from '../../base-form.component';
import { FormStep } from '../../../../../models/forms/form-steps.interface';
import { SupervisorInjuryReport } from '../../../../../models/forms/supervisor-injury-report/supervisor-injury-report.model';
import { FormService } from '../../../../../services/form.service';
import { EntraService } from '../../../../../services/entra.service';
import { AttachmentUploadService } from '../../../../../services/attachment-upload.service';
import { SignatureService } from '../../../../../services/signature.service';
import { ToastService } from '../../../../../services/toast.service';
import { ScreenSizeService } from '../../../../../services/screen-size.service';
import { FormModelService } from '../../../../../services/form-model.service';
import { phoneNumberValidator, simpleCheckboxValidator } from '../../../../../utils/form-validators';

/**
 * Component for handling Supervisor Injury Report submissions.
 * This form is used by supervisors to document workplace injuries,
 * ensuring proper safety reporting and analysis.
 */
@Component({
  selector: 'app-supervisor-injury-report',
  templateUrl: './supervisor-injury-report.component.html',
  styleUrls: ['./supervisor-injury-report.component.css'],
})
export class SupervisorInjuryReportComponent
  extends BaseFormComponent<SupervisorInjuryReport>
  implements OnInit
{
  // Form type identifier used for routing and form metadata lookup
  override formType = SupervisorInjuryReport.getFormInfo().formType;

  // Instance of the form data model
  override model = new SupervisorInjuryReport();

  // Define the form steps and their properties
  override steps: FormStep[] = SupervisorInjuryReport.getFormSteps();

  incidentCausesCheckboxes: { name: string; label: string }[] = [];

  constructor(
    protected override fb: FormBuilder,
    protected override formService: FormService,
    protected override router: Router,
    protected override entraService: EntraService,
    protected override attachmentService: AttachmentUploadService,
    protected override toastService: ToastService,
    protected override signatureService: SignatureService,
    protected override screenSizeService: ScreenSizeService,
    protected formModelService: FormModelService
  ) {
    super(
      fb,
      formService,
      router,
      entraService,
      attachmentService,
      toastService,
      signatureService,
      screenSizeService
    );

    // Initialize form metadata first
    this.formMetadata = this.formModelService.getFormMetadata(this.formType)!;
  }

  override ngOnInit(): void {
    super.ngOnInit();
  }

  // Static methods to get the checkbox groupings for the component
  getIncidentCausesCheckboxes(): { name: string; label: string }[] {
    return [
      { name: 'awkwardBodyPositioning', label: this.fieldMappings['awkwardBodyPositioning'] },
      { name: 'causedByThirdParty', label: this.fieldMappings['causedByThirdParty'] },
      { name: 'complacency', label: this.fieldMappings['complacency'] },
      { name: 'incorrectTool', label: this.fieldMappings['incorrectTool'] },
      { name: 'materialFailure', label: this.fieldMappings['materialFailure'] },
      { name: 'equipmentToolFailure', label: this.fieldMappings['equipmentToolFailure'] },
      { name: 'failureToMeetTargets', label: this.fieldMappings['failureToMeetTargets'] },
      { name: 'failureToUsePpe', label: this.fieldMappings['failureToUsePpe'] },
      { name: 'lackOfFallProtection', label: this.fieldMappings['lackOfFallProtection'] },
      { name: 'improperLiftingTechnique', label: this.fieldMappings['improperLiftingTechnique'] },
      { name: 'improperLadderUsage', label: this.fieldMappings['improperLadderUsage'] },
      { name: 'ignoringSafeWorkProcedures', label: this.fieldMappings['ignoringSafeWorkProcedures'] },
      { name: 'lackOfKnowledgeTraining', label: this.fieldMappings['lackOfKnowledgeTraining'] },
      { name: 'notCommunicating', label: this.fieldMappings['notCommunicating'] },
      { name: 'numerousMinorInjuries', label: this.fieldMappings['numerousMinorInjuries'] },
      { name: 'occupationalIllness', label: this.fieldMappings['occupationalIllness'] },
      { name: 'physicalViolence', label: this.fieldMappings['physicalViolence'] },
      { name: 'poorHousekeeping', label: this.fieldMappings['poorHousekeeping'] },
      { name: 'poorVisibilityLighting', label: this.fieldMappings['poorVisibilityLighting'] },
      { name: 'preoccupied', label: this.fieldMappings['preoccupied'] },
      { name: 'slippingTripping', label: this.fieldMappings['slippingTripping'] },
      { name: 'supervisorManagementFailure', label: this.fieldMappings['supervisorManagementFailure'] },
      { name: 'unresolvedIssues', label: this.fieldMappings['unresolvedIssues'] },
      { name: 'willfulRuleViolation', label: this.fieldMappings['willfulRuleViolation'] },
      { name: 'otherCause', label: this.fieldMappings['otherCause'] },
    ];
  }

  /**
   * Initializes the form with its controls and validators.
   * Sets up the form group with all required fields and their validation rules.
   */
  protected override initializeForm(): void {
    this.incidentCausesCheckboxes = this.getIncidentCausesCheckboxes();
    
    // Create form groups for checkboxes with validators
    const incidentCausesGroup = this.fb.group(
      {},
      {
        validators: [simpleCheckboxValidator(1)], // Require at least 1 incident cause
      }
    );
    this.incidentCausesCheckboxes.forEach((checkbox) => {
      incidentCausesGroup.addControl(checkbox.name, this.fb.control(false));
    });

    this.form = this.fb.group({
      employeeDetails: this.fb.group({
        // Employee Information
        employeeFirstName: [this.model.employeeFirstName, Validators.required],
        employeeLastName: [this.model.employeeLastName, Validators.required],
        streetAddress: [this.model.streetAddress, Validators.required],
        city: [this.model.city, Validators.required],
        stateProvince: [this.model.stateProvince, Validators.required],
        zipPostalCode: [this.model.zipPostalCode, Validators.required],
        phone: [this.model.phone,  [Validators.required, phoneNumberValidator()]],
        dateOfHire: [this.model.dateOfHire, Validators.required],
        craftAndPosition: [this.model.craftAndPosition, Validators.required],
      }),
      jobDetails: this.fb.group({
        // Job Information
        jobName: [this.model.jobName, Validators.required],
        shiftWorked: [this.model.shiftWorked, Validators.required],
        supervisorFirstName: [this.model.supervisorFirstName, Validators.required],
        supervisorLastName: [this.model.supervisorLastName, Validators.required],
        hoursWorkedPerDay: [this.model.hoursWorkedPerDay, Validators.required],
        hoursWorkedPerWeek: [this.model.hoursWorkedPerWeek, Validators.required],
      }),
      incidentDetails: this.fb.group({
        // Incident Information
        typeOfIncident: [this.model.typeOfIncident, Validators.required],
        dateOfIncident: [this.model.dateOfIncident, Validators.required],
        timeOfIncident: [this.model.timeOfIncident, Validators.required],
        dateReported: [this.model.dateReported, Validators.required],
        timeReported: [this.model.timeReported, Validators.required],
        incidentLocation: [this.model.incidentLocation, Validators.required],
        weather: [this.model.weather, Validators.required],
        temperature: [this.model.temperature, Validators.required],
        equipment1: [this.model.equipment1, Validators.required],
        equipment2: [this.model.equipment2],
        anyDefects: [this.model.anyDefects],
        coveredInPreTask: [this.model.coveredInPreTask, Validators.required],
        witness1: [this.model.witness1, Validators.required],
        witness2: [this.model.witness2],
      }),
      incidentAnalysis: this.fb.group({
        // Analysis
        employeeActivity: [this.model.employeeActivity, Validators.required],
        contributingFactors: [this.model.contributingFactors, Validators.required],
        safetyProcedures: [this.model.safetyProcedures, Validators.required],
        employeeFamiliarWithProcedures: [this.model.employeeFamiliarWithProcedures, Validators.required],
        employeeFollowedProcedures: [this.model.employeeFollowedProcedures, Validators.required],
        whyNotFollowed: [this.model.whyNotFollowed],
        
        // Incident Causes
        incidentCauses: incidentCausesGroup,
        otherCauseSpecify: [this.model.otherCauseSpecify],
        incidentCausesDetail: [this.model.incidentCausesDetail, Validators.required],
      }),
      injuryAssessment: this.fb.group({
        // Injury Information
        injured: [this.model.injured, Validators.required],
        bloodExposure: [this.model.bloodExposure, Validators.required],
        seekMedicalTreatment: [this.model.seekMedicalTreatment, Validators.required],
        treatmentFacilityName: [this.model.treatmentFacilityName],
        doctorFirstName: [this.model.doctorFirstName],
        doctorLastName: [this.model.doctorLastName],
        doctorContactInfo: [this.model.doctorContactInfo],
        propertyDamage: [this.model.propertyDamage, Validators.required],
        othersInvolved: [this.model.othersInvolved, Validators.required],
        preventionSuggestion: [this.model.preventionSuggestion, Validators.required],
      }),
      signOff: this.fb.group({
        signatureDate: [this.model.signatureDate],
      }),
    });

    this.setupConditionalValidators();
  }

  // Form group getters
  get employeeDetailsFormGroup(): FormGroup {
    return this.form.get('employeeDetails') as FormGroup;
  }

  get jobDetailsFormGroup(): FormGroup {
    return this.form.get('jobDetails') as FormGroup;
  }

  get incidentDetailsFormGroup(): FormGroup {
    return this.form.get('incidentDetails') as FormGroup;
  }

  get incidentAnalysisFormGroup(): FormGroup {
    return this.form.get('incidentAnalysis') as FormGroup;
  }

  get incidentCausesCheckboxesFormGroup(): FormGroup {
    return this.incidentAnalysisFormGroup.get('incidentCauses') as FormGroup;
  }

  get injuryAssessmentFormGroup(): FormGroup {
    return this.form.get('injuryAssessment') as FormGroup;
  }

  get signOffFormGroup(): FormGroup {
    return this.form.get('signOff') as FormGroup;
  }

  /**
   * Retrieves the current form data and creates a new SupervisorInjuryReport instance.
   * This method is called when submitting the form.
   */
  protected override getFormData(): SupervisorInjuryReport {
    const formValue = this.form.value;

    // Flatten the nested form groups into a single object
    const flattenedData = {
      // Employee Information
      ...formValue.employeeDetails,

      // Job Information
      ...formValue.jobDetails,

      // Incident Details
      ...formValue.incidentDetails,

      // Incident Analysis (including checkbox group)
      employeeActivity: formValue.incidentAnalysis.employeeActivity,
      contributingFactors: formValue.incidentAnalysis.contributingFactors,
      safetyProcedures: formValue.incidentAnalysis.safetyProcedures,
      employeeFamiliarWithProcedures: formValue.incidentAnalysis.employeeFamiliarWithProcedures,
      employeeFollowedProcedures: formValue.incidentAnalysis.employeeFollowedProcedures,
      whyNotFollowed: formValue.incidentAnalysis.whyNotFollowed,
      ...formValue.incidentAnalysis.incidentCauses,
      otherCauseSpecify: formValue.incidentAnalysis.otherCauseSpecify,
      incidentCausesDetail: formValue.incidentAnalysis.incidentCausesDetail,

      // Injury Assessment
      ...formValue.injuryAssessment,

      // Sign Off
      ...formValue.signOff,
    };
    const supervisorInjuryReport = new SupervisorInjuryReport(flattenedData);
    const updatedSupervisorInjuryReport = this.updateModelWithAttachments(supervisorInjuryReport);
    return updatedSupervisorInjuryReport;
  }

  /**
   * Sets up validation rules for each step of the supervisor injury report form.
   */
   protected override setupDefaultStepValidation(): void {
    // Skip validation setup if noFormRequirements is true
    if (!this.shouldApplyFormRequirements()) {
      return;
    }
    // Define step indices for attachments and signatures
    const UPLOAD_REQUIREMENT_INDEX: number = 0; // Upload requirement at index 0
    const SIGNATURE_REQUIREMENT_INDEX: number = 0; // Signature requirement at index 0

    // Custom validation rules for each step using the form groups directly
    this.setupStepValidation('employee-details', this.employeeDetailsFormGroup);
    this.setupStepValidation('job-details', this.jobDetailsFormGroup);
    this.setupStepValidation('incident-details', this.incidentDetailsFormGroup);
    this.setupStepValidation('incident-analysis', this.incidentAnalysisFormGroup);
    this.setupStepValidation('injury-assessment', this.injuryAssessmentFormGroup, {
      attachmentIndex: [UPLOAD_REQUIREMENT_INDEX],
    });
    this.setupStepValidation('sign-off', this.signOffFormGroup, {
      attachmentIndex: [UPLOAD_REQUIREMENT_INDEX],
      signatureIndex: [SIGNATURE_REQUIREMENT_INDEX],
    });
  }

  /**
   * Sets up conditional validation for various fields.
   */
  private setupConditionalValidators(): void {
    this.setupEmployeeFollowedProceduresValidator();
    this.setupOtherCauseValidator();
    this.setupMedicalTreatmentValidators();
  }

  private setupEmployeeFollowedProceduresValidator(): void {
    this.incidentAnalysisFormGroup
      .get('employeeFollowedProcedures')
      ?.valueChanges.subscribe((value) => {
        const control = this.incidentAnalysisFormGroup.get('whyNotFollowed');
        if (value === 'No') {
          control?.setValidators([Validators.required]);
        } else {
          control?.clearValidators();
        }
        control?.updateValueAndValidity();
      });
  }

  private setupOtherCauseValidator(): void {
    this.incidentCausesCheckboxesFormGroup
      .get('otherCause')
      ?.valueChanges.subscribe((checked) => {
        const control = this.incidentAnalysisFormGroup.get('otherCauseSpecify');
        if (checked) {
          control?.setValidators([Validators.required]);
        } else {
          control?.clearValidators();
        }
        control?.updateValueAndValidity();
      });
  }

  private setupMedicalTreatmentValidators(): void {
    this.injuryAssessmentFormGroup
      .get('seekMedicalTreatment')
      ?.valueChanges.subscribe((value) => {
        const facilityControl = this.injuryAssessmentFormGroup.get('treatmentFacilityName');
        const doctorFirstControl = this.injuryAssessmentFormGroup.get('doctorFirstName');
        const doctorLastControl = this.injuryAssessmentFormGroup.get('doctorLastName');
        const doctorContactControl = this.injuryAssessmentFormGroup.get('doctorContactInfo');
        
        if (value === 'Yes') {
          facilityControl?.setValidators([Validators.required]);
          doctorFirstControl?.setValidators([Validators.required]);
          doctorLastControl?.setValidators([Validators.required]);
          doctorContactControl?.setValidators([Validators.required]);
        } else {
          facilityControl?.clearValidators();
          doctorFirstControl?.clearValidators();
          doctorLastControl?.clearValidators();
          doctorContactControl?.clearValidators();
        }
        
        facilityControl?.updateValueAndValidity();
        doctorFirstControl?.updateValueAndValidity();
        doctorLastControl?.updateValueAndValidity();
        doctorContactControl?.updateValueAndValidity();
      });
  }
}